import React, { useEffect, useState, useMemo, useRef } from 'react';
import { getMasterData, addMasterData, updateMasterData, deleteMasterData, getTransporters, createTransporter, updateTransporter, deleteTransporter, getFabricTypes, addFabricType } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { toast } from 'sonner';
import { Plus, Trash2, Database, Truck, Pencil, MapPin, Users, Factory, Tag, ArrowLeft, Search, Download, Clock, Store } from 'lucide-react';
import { useAuth } from '../AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import ExportDialog from '../components/ExportDialog';
import RecordHistoryDialog from '../components/RecordHistoryDialog';
import useExportShortcut from '../hooks/useExportShortcut';
import { useScreenState, useScrollRestoration } from '../hooks/useScreenState';
import useSaveShortcut from '../hooks/useSaveShortcut';
import { exportToExcel, exportToPDF, EXPORT_COLUMNS } from '../utils/exportUtils';

// Indian cities list with important textile hubs first
const INDIAN_CITIES = [
  'Ahmedabad', 'Surat', 'Ichalkaranji', 'Bhiwandi', 'Salem', 'Erode', 'Mumbai', 'Delhi',
  'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Jaipur', 'Lucknow', 'Kanpur',
  'Nagpur', 'Indore', 'Thane', 'Bhopal', 'Visakhapatnam', 'Patna', 'Vadodara', 'Ghaziabad',
  'Ludhiana', 'Agra', 'Nashik', 'Faridabad', 'Meerut', 'Rajkot', 'Varanasi', 'Srinagar',
  'Aurangabad', 'Dhanbad', 'Amritsar', 'Allahabad', 'Ranchi', 'Howrah', 'Coimbatore',
  'Jabalpur', 'Gwalior', 'Vijayawada', 'Jodhpur', 'Madurai', 'Raipur', 'Kota', 'Guwahati',
  'Chandigarh', 'Solapur', 'Hubli', 'Tiruchirappalli', 'Tiruppur', 'Moradabad', 'Mysore',
  'Bareilly', 'Aligarh', 'Jalandhar', 'Bhubaneswar', 'Warangal', 'Guntur', 'Bikaner',
  'Amravati', 'Noida', 'Jamshedpur', 'Bhilai', 'Cuttack', 'Firozabad', 'Kochi', 'Nellore',
  'Bhavnagar', 'Dehradun', 'Durgapur', 'Asansol', 'Rourkela', 'Nanded', 'Kolhapur', 'Ajmer',
  'Akola', 'Gulbarga', 'Jamnagar', 'Ujjain', 'Loni', 'Siliguri', 'Jhansi', 'Ulhasnagar',
  'Jammu', 'Sangli', 'Mangalore', 'Other'
];

// Common textile markets
const TEXTILE_MARKETS = [
  'Ring Road', 'Textile Market', 'Cloth Market', 'Mill Area', 'Industrial Area',
  'GIDC', 'MIDC', 'SEZ', 'Main Market', 'Wholesale Market', 'Other'
];

// Category configuration
const CATEGORIES = {
  buyerMarkets: {
    title: 'Buyer Markets',
    icon: Store,
    color: 'purple',
    bgColor: 'bg-purple-500',
    hoverBg: 'hover:bg-purple-50',
    hoverBorder: 'hover:border-purple-400',
    borderColor: 'border-purple-500',
    buttonColor: 'bg-purple-600 hover:bg-purple-700',
    description: 'Manage market locations for buyers'
  },
  buyers: {
    title: 'Buyers',
    icon: Users,
    color: 'emerald',
    bgColor: 'bg-emerald-500',
    hoverBg: 'hover:bg-emerald-50',
    hoverBorder: 'hover:border-emerald-400',
    borderColor: 'border-emerald-500',
    buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
    description: 'Manage buyer contacts and details'
  },
  suppliers: {
    title: 'Suppliers',
    icon: Factory,
    color: 'amber',
    bgColor: 'bg-amber-500',
    hoverBg: 'hover:bg-amber-50',
    hoverBorder: 'hover:border-amber-400',
    borderColor: 'border-amber-500',
    buttonColor: 'bg-amber-600 hover:bg-amber-700',
    description: 'Manage supplier information with address details'
  },
  tradeNames: {
    title: 'Trade Names',
    icon: Tag,
    color: 'rose',
    bgColor: 'bg-rose-500',
    hoverBg: 'hover:bg-rose-50',
    hoverBorder: 'hover:border-rose-400',
    borderColor: 'border-rose-500',
    buttonColor: 'bg-rose-600 hover:bg-rose-700',
    description: 'Manage fabric trade names with type and unit'
  },
  transporters: {
    title: 'Transporters',
    icon: Truck,
    color: 'cyan',
    bgColor: 'bg-cyan-500',
    hoverBg: 'hover:bg-cyan-50',
    hoverBorder: 'hover:border-cyan-400',
    borderColor: 'border-cyan-500',
    buttonColor: 'bg-cyan-600 hover:bg-cyan-700',
    description: 'Manage transporter routes and status'
  }
};

export default function MasterData() {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useScreenState('masterData.selectedCategory', null);
  const [searchQuery, setSearchQuery] = useScreenState('masterData.searchQuery', '');
  
  const [buyers, setBuyers] = useState([]);
  const [buyerMarkets, setBuyerMarkets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tradeNames, setTradeNames] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [fabricTypes, setFabricTypes] = useState(['Printed', 'Dyed', 'Yarn Dyed']);
  const [loading, setLoading] = useState(true);

  const [newBuyer, setNewBuyer] = useState('');
  const [newBuyerMarket, setNewBuyerMarket] = useState('');
  
  // Buyer form state (with market selection)
  const [buyerDialogOpen, setBuyerDialogOpen] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [buyerForm, setBuyerForm] = useState({
    name: '', market_id: ''
  });
  
  // Trade Name form state
  const [tradeNameDialogOpen, setTradeNameDialogOpen] = useState(false);
  const [editingTradeName, setEditingTradeName] = useState(null);
  const [tradeNameForm, setTradeNameForm] = useState({
    name: '', supplier_id: '', fabric_type: 'Printed', si_unit: 'Metre', is_active: true,
    dyed_ordering_mode: '', default_matching: ''
  });
  const [newFabricTypeInput, setNewFabricTypeInput] = useState('');

  // Supplier form state
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({
    name: '', address: '', city: '', market: '', default_purchase_cd_percent: ''
  });

  // Transporter form state
  const [transporterDialogOpen, setTransporterDialogOpen] = useState(false);
  const [editingTransporter, setEditingTransporter] = useState(null);
  const [transporterForm, setTransporterForm] = useState({
    name: '', source: '', final_destination: '', is_active: true
  });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: '', itemId: '', itemName: '' });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [historyDialog, setHistoryDialog] = useState({ open: false, record: null });

  // ALT+E shortcut for export
  useExportShortcut(() => {
    if (selectedCategory) {
      setExportDialogOpen(true);
    }
  });

  // Check if user can manage master data - same logic as access check
  const canManageMasterDataCheck = () => {
    if (user?.role === 'admin' || user?.role === 'Super Admin') return true;
    const allowedUsers = ['jay', 'jagruti', 'shruti'];
    return allowedUsers.includes(user?.username?.toLowerCase());
  };

  useEffect(() => {
    if (canManageMasterDataCheck()) {
      fetchAllData();
    }
  }, [user]);

  // Reset search when category changes (but not on initial mount/restore,
  // including React.StrictMode's double-invoked mount effect)
  const prevCategoryRef = useRef(selectedCategory);
  useEffect(() => {
    if (prevCategoryRef.current !== selectedCategory) {
      setSearchQuery('');
      prevCategoryRef.current = selectedCategory;
    }
  }, [selectedCategory]);

  useScrollRestoration('masterData', !loading);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [buyersRes, buyerMarketsRes, suppliersRes, tradeNamesRes, transportersRes, fabricTypesRes] = await Promise.all([
        getMasterData('buyer'),
        getMasterData('buyer_market'),
        getMasterData('supplier'),
        getMasterData('trade_name'),
        getTransporters(),
        getFabricTypes(),
      ]);
      setBuyers(buyersRes.data);
      setBuyerMarkets(buyerMarketsRes.data);
      setSuppliers(suppliersRes.data);
      setTradeNames(tradeNamesRes.data);
      setTransporters(transportersRes.data);
      if (fabricTypesRes.data?.fabric_types) {
        setFabricTypes(fabricTypesRes.data.fabric_types);
      }
    } catch (error) {
      console.error('Failed to fetch master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  // Filtered lists based on search
  const filteredBuyers = useMemo(() => 
    buyers.filter(b => 
      b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.market_name && b.market_name.toLowerCase().includes(searchQuery.toLowerCase()))
    ),
    [buyers, searchQuery]
  );

  const filteredBuyerMarkets = useMemo(() => 
    buyerMarkets.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [buyerMarkets, searchQuery]
  );
  
  const filteredSuppliers = useMemo(() => 
    suppliers.filter(s => 
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.city && s.city.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (s.market && s.market.toLowerCase().includes(searchQuery.toLowerCase()))
    ),
    [suppliers, searchQuery]
  );
  
  const filteredTradeNames = useMemo(() => 
    tradeNames.filter(t => 
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.fabric_type && t.fabric_type.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.supplier_name && t.supplier_name.toLowerCase().includes(searchQuery.toLowerCase()))
    ),
    [tradeNames, searchQuery]
  );
  
  const filteredTransporters = useMemo(() => 
    transporters.filter(t => 
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.source && t.source.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.final_destination && t.final_destination.toLowerCase().includes(searchQuery.toLowerCase()))
    ),
    [transporters, searchQuery]
  );

  // Get export data based on selected category
  const getExportData = (scope) => {
    const categoryMap = {
      buyerMarkets: { all: buyerMarkets, filtered: filteredBuyerMarkets, columns: EXPORT_COLUMNS.buyerMarkets || EXPORT_COLUMNS.buyers, name: 'Buyer Markets' },
      buyers: { all: buyers, filtered: filteredBuyers, columns: EXPORT_COLUMNS.buyers, name: 'Buyers' },
      suppliers: { all: suppliers, filtered: filteredSuppliers, columns: EXPORT_COLUMNS.suppliers, name: 'Suppliers' },
      tradeNames: { all: tradeNames, filtered: filteredTradeNames, columns: EXPORT_COLUMNS.tradeNames, name: 'Trade Names' },
      transporters: { all: transporters, filtered: filteredTransporters, columns: EXPORT_COLUMNS.transporters, name: 'Transporters' },
    };
    return categoryMap[selectedCategory] || null;
  };

  // Export handler
  const handleExport = async (format, scope) => {
    const exportData = getExportData(scope);
    if (!exportData) {
      return { success: false, message: 'Please select a category to export.' };
    }

    const dataToExport = scope === 'current' ? exportData.filtered : exportData.all;
    
    if (dataToExport.length === 0) {
      return { success: false, message: 'No data available to export.' };
    }

    // Add index to data for Sr. No.
    const dataWithIndex = dataToExport.map((item, idx) => ({ ...item, _idx: idx }));
    const columnsWithIndex = exportData.columns.map(col => ({
      ...col,
      accessor: col.header === 'Sr. No.' ? (item) => item._idx + 1 : col.accessor
    }));

    const filterDesc = searchQuery ? [`Search: ${searchQuery}`] : [];

    if (format === 'excel') {
      return exportToExcel(dataWithIndex, columnsWithIndex, exportData.name, exportData.name);
    } else {
      return exportToPDF(dataWithIndex, columnsWithIndex, exportData.name, `${exportData.name} Report`, filterDesc);
    }
  };

  const handleAdd = async (category, name, setName, setData, additionalData = {}) => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      const payload = { name: name.trim(), ...additionalData };
      const response = await addMasterData(category, payload);
      setData((prev) => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setName('');
      toast.success(`${name} added successfully`);
    } catch (error) {
      console.error('Failed to add:', error);
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleDeleteClick = (category, itemId, itemName) => {
    setDeleteDialog({ open: true, category, itemId, itemName });
  };

  const handleDeleteConfirm = async () => {
    const { category, itemId } = deleteDialog;
    try {
      if (category === 'transporter') {
        await deleteTransporter(itemId);
        setTransporters((prev) => prev.filter((item) => item.id !== itemId));
      } else {
        await deleteMasterData(category, itemId);
        if (category === 'buyer') setBuyers((prev) => prev.filter((item) => item.id !== itemId));
        else if (category === 'buyer_market') setBuyerMarkets((prev) => prev.filter((item) => item.id !== itemId));
        else if (category === 'supplier') setSuppliers((prev) => prev.filter((item) => item.id !== itemId));
        else if (category === 'trade_name') setTradeNames((prev) => prev.filter((item) => item.id !== itemId));
      }
      toast.success('Item deleted successfully');
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    } finally {
      setDeleteDialog({ open: false, category: '', itemId: '', itemName: '' });
    }
  };

  // Transporter handlers
  const openAddTransporterDialog = () => {
    setEditingTransporter(null);
    setTransporterForm({ name: '', source: '', final_destination: '', is_active: true });
    setTransporterDialogOpen(true);
  };

  const openEditTransporterDialog = (transporter) => {
    setEditingTransporter(transporter);
    setTransporterForm({
      name: transporter.name,
      source: transporter.source,
      final_destination: transporter.final_destination,
      is_active: transporter.is_active
    });
    setTransporterDialogOpen(true);
  };

  const handleSaveTransporter = async () => {
    if (!transporterForm.name.trim()) { toast.error('Name is required'); return; }
    if (!transporterForm.source.trim()) { toast.error('Source is required'); return; }
    if (!transporterForm.final_destination.trim()) { toast.error('Final Destination is required'); return; }

    try {
      if (editingTransporter) {
        const response = await updateTransporter(editingTransporter.id, transporterForm);
        setTransporters(prev => prev.map(t => t.id === editingTransporter.id ? response.data : t));
        toast.success('Transporter updated successfully');
      } else {
        const response = await createTransporter(transporterForm);
        setTransporters(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Transporter added successfully');
      }
      setTransporterDialogOpen(false);
    } catch (error) {
      console.error('Failed to save transporter:', error);
      toast.error(error.response?.data?.detail || 'Failed to save transporter');
    }
  };

  // Supplier handlers
  const openAddSupplierDialog = () => {
    setEditingSupplier(null);
    setSupplierForm({ name: '', address: '', city: '', market: '', default_purchase_cd_percent: '' });
    setSupplierDialogOpen(true);
  };

  const openEditSupplierDialog = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name,
      address: supplier.address || '',
      city: supplier.city || '',
      market: supplier.market || '',
      default_purchase_cd_percent: supplier.default_purchase_cd_percent != null ? String(supplier.default_purchase_cd_percent) : ''
    });
    setSupplierDialogOpen(true);
  };

  const handleSaveSupplier = async () => {
    if (!supplierForm.name.trim()) { toast.error('Supplier name is required'); return; }

    try {
      const payload = {
        ...supplierForm,
        default_purchase_cd_percent: supplierForm.default_purchase_cd_percent !== '' ? parseFloat(supplierForm.default_purchase_cd_percent) : 0
      };
      if (editingSupplier) {
        const response = await updateMasterData('supplier', editingSupplier.id, payload);
        setSuppliers(prev => prev.map(s => s.id === editingSupplier.id ? response.data : s));
        toast.success('Supplier updated successfully');
      } else {
        const response = await addMasterData('supplier', payload);
        setSuppliers(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Supplier added successfully');
      }
      setSupplierDialogOpen(false);
    } catch (error) {
      console.error('Failed to save supplier:', error);
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    }
  };

  // Trade Name handlers
  const openAddTradeNameDialog = () => {
    setEditingTradeName(null);
    setTradeNameForm({ 
      name: '', supplier_id: '__unassigned__', fabric_type: 'Printed', si_unit: 'Metre', is_active: true,
      dyed_ordering_mode: '', default_matching: ''
    });
    setTradeNameDialogOpen(true);
  };

  const openEditTradeNameDialog = (tradeName) => {
    setEditingTradeName(tradeName);
    setTradeNameForm({
      name: tradeName.name,
      supplier_id: tradeName.supplier_id || '__unassigned__',  // Use placeholder for no supplier
      fabric_type: tradeName.fabric_type || 'Printed',
      si_unit: tradeName.si_unit || 'Metre',
      is_active: tradeName.is_active !== false,  // Default to true for backward compatibility
      dyed_ordering_mode: tradeName.dyed_ordering_mode || '',
      default_matching: tradeName.default_matching || ''
    });
    setTradeNameDialogOpen(true);
  };

  const handleSaveTradeName = async () => {
    if (!tradeNameForm.name.trim()) { toast.error('Trade name is required'); return; }
    if (!tradeNameForm.fabric_type) { toast.error('Fabric type is required'); return; }
    if (!tradeNameForm.si_unit) { toast.error('Unit is required'); return; }
    
    // Validate dyed ordering mode for Dyed fabric type
    if (tradeNameForm.fabric_type === 'Dyed' && !tradeNameForm.dyed_ordering_mode) {
      toast.error('Dyed Ordering Mode is required for Dyed trade names');
      return;
    }
    
    // Validate default matching for Fixed Chart mode
    if (tradeNameForm.dyed_ordering_mode === 'fixed_chart' && !tradeNameForm.default_matching) {
      toast.error('Default Matching is required for Fixed Chart mode');
      return;
    }

    try {
      // Convert "__unassigned__" placeholder back to null for API
      const supplierId = tradeNameForm.supplier_id === '__unassigned__' ? null : tradeNameForm.supplier_id;
      
      const payload = {
        name: tradeNameForm.name.trim(),
        supplier_id: supplierId || null,
        fabric_type: tradeNameForm.fabric_type,
        si_unit: tradeNameForm.si_unit,
        is_active: tradeNameForm.is_active,
        // Only include dyed fields for Dyed fabric type
        ...(tradeNameForm.fabric_type === 'Dyed' && {
          dyed_ordering_mode: tradeNameForm.dyed_ordering_mode || null,
          default_matching: tradeNameForm.dyed_ordering_mode === 'fixed_chart' 
            ? parseInt(tradeNameForm.default_matching, 10) || null 
            : null
        })
      };

      if (editingTradeName) {
        const response = await updateMasterData('trade_name', editingTradeName.id, payload);
        setTradeNames(prev => prev.map(t => t.id === editingTradeName.id ? response.data : t));
        toast.success('Trade name updated successfully');
      } else {
        const response = await addMasterData('trade_name', payload);
        setTradeNames(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Trade name added successfully');
      }
      setTradeNameDialogOpen(false);
    } catch (error) {
      console.error('Failed to save trade name:', error);
      toast.error(error.response?.data?.detail || 'Failed to save trade name');
    }
  };

  const handleAddFabricType = async () => {
    if (!newFabricTypeInput.trim()) { toast.error('Fabric type cannot be empty'); return; }
    try {
      const response = await addFabricType(newFabricTypeInput.trim());
      setFabricTypes(response.data.fabric_types);
      setNewFabricTypeInput('');
      toast.success('Fabric type added successfully');
    } catch (error) {
      console.error('Failed to add fabric type:', error);
      toast.error(error.response?.data?.detail || 'Failed to add fabric type');
    }
  };

  // Buyer handlers
  const handleSaveBuyer = async () => {
    if (!buyerForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!buyerForm.market_id) {
      toast.error('Market is required. Please select a buyer market.');
      return;
    }

    try {
      if (editingBuyer) {
        const response = await updateMasterData('buyer', editingBuyer.id, buyerForm);
        setBuyers(prev => prev.map(b => b.id === editingBuyer.id ? response.data : b));
        toast.success('Buyer updated successfully');
      } else {
        const response = await addMasterData('buyer', buyerForm);
        setBuyers(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Buyer added successfully');
      }
      setBuyerDialogOpen(false);
    } catch (error) {
      console.error('Failed to save buyer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save buyer');
    }
  };

  // CTRL+S saves whichever Master Data create/edit form is currently active
  useSaveShortcut(() => {
    if (buyerDialogOpen) return handleSaveBuyer();
    if (tradeNameDialogOpen) return handleSaveTradeName();
    if (supplierDialogOpen) return handleSaveSupplier();
    if (transporterDialogOpen) return handleSaveTransporter();
    if (selectedCategory === 'buyerMarkets' && newBuyerMarket.trim()) {
      return handleAdd('buyer_market', newBuyerMarket, setNewBuyerMarket, setBuyerMarkets);
    }
  });

  // Access check - Allow admins + specific authorized users
  const canManageMasterData = () => {
    if (user?.role === 'admin' || user?.role === 'Super Admin') return true;
    const allowedUsers = ['jay', 'jagruti', 'shruti'];
    return allowedUsers.includes(user?.username?.toLowerCase());
  };

  if (!canManageMasterData()) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Database className="h-16 w-16 text-stone-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-maroon-950 mb-2">Access Denied</h2>
          <p className="text-maroon-700">You don&apos;t have permission to manage master data</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-maroon-600">Loading master data...</div>
      </div>
    );
  }

  // ==================== MENU CARDS VIEW ====================
  if (!selectedCategory) {
    return (
      <div className="space-y-6" data-testid="master-data-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-maroon-950">Master Data</h1>
          <p className="text-maroon-700 mt-1">Select a category to manage</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Object.entries(CATEGORIES).map(([key, config]) => {
            const Icon = config.icon;
            const count = key === 'buyerMarkets' ? buyerMarkets.length
                        : key === 'buyers' ? buyers.length 
                        : key === 'suppliers' ? suppliers.length 
                        : key === 'tradeNames' ? tradeNames.length 
                        : transporters.length;
            
            return (
              <Card 
                key={key}
                data-testid={`master-card-${key}`}
                className={`cursor-pointer border-2 border-maroon-200 ${config.hoverBg} ${config.hoverBorder} transition-all duration-200 hover:shadow-lg`}
                onClick={() => setSelectedCategory(key)}
              >
                <CardContent className="p-6 lg:p-5">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${config.bgColor} text-white`}>
                      <Icon className="h-8 w-8" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-maroon-950">{config.title}</h3>
                      <p className="text-sm text-maroon-600 mt-1">{config.description}</p>
                      <div className="mt-3 flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${config.bgColor} text-white`}>
                          {count} {count === 1 ? 'entry' : 'entries'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // ==================== INDIVIDUAL MASTER VIEW ====================
  const currentConfig = CATEGORIES[selectedCategory];
  const Icon = currentConfig.icon;

  // Common header for all master pages
  const renderHeader = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedCategory(null)}
          className="text-maroon-700 hover:bg-maroon-100 -ml-2"
          data-testid="back-to-menu-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Master Data
        </Button>
        <Button
          variant="outline"
          onClick={() => setExportDialogOpen(true)}
          className="border-maroon-300 text-maroon-700 hover:bg-maroon-50"
          data-testid="export-button"
          title="Export (Alt+E)"
        >
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>
      
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${currentConfig.bgColor} text-white`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-maroon-950">{currentConfig.title} Master</h1>
          <p className="text-sm text-maroon-600">{currentConfig.description}</p>
        </div>
      </div>
    </div>
  );

  // Common search bar
  const renderSearchBar = () => (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-maroon-400" />
      <Input
        data-testid="master-search-input"
        placeholder={`Search ${currentConfig.title.toLowerCase()}...`}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="pl-10"
      />
    </div>
  );

  // ==================== BUYER MARKET VIEW ====================
  if (selectedCategory === 'buyerMarkets') {
    return (
      <div className="space-y-4" data-testid="buyer-markets-master-page">
        {renderHeader()}
        
        <Card className={`border-l-4 ${currentConfig.borderColor}`}>
          <CardContent className="p-4 lg:p-3 space-y-4">
            {/* Search */}
            {renderSearchBar()}
            
            {/* Add new buyer market */}
            <div className="flex gap-2">
              <Input
                data-testid="new-buyer-market-input"
                placeholder="Enter new market name..."
                value={newBuyerMarket}
                onChange={(e) => setNewBuyerMarket(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAdd('buyer_market', newBuyerMarket, setNewBuyerMarket, setBuyerMarkets)}
                className="flex-1"
              />
              <Button
                data-testid="add-buyer-market-button"
                onClick={() => handleAdd('buyer_market', newBuyerMarket, setNewBuyerMarket, setBuyerMarkets)}
                className={currentConfig.buttonColor}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            
            {/* List */}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {filteredBuyerMarkets.length === 0 ? (
                <p className="text-sm text-maroon-600 text-center py-8">
                  {searchQuery ? 'No markets match your search' : 'No buyer markets added yet. Add markets before creating buyers.'}
                </p>
              ) : (
                filteredBuyerMarkets.map((market) => {
                  const buyersInMarket = buyers.filter(b => b.market_id === market.id).length;
                  return (
                    <div
                      key={market.id}
                      className="flex items-center justify-between p-3 border border-maroon-200 rounded-lg hover:bg-maroon-50"
                    >
                      <div>
                        <span className="font-medium text-maroon-950">{market.name}</span>
                        <span className="text-xs text-gray-500 ml-2">({buyersInMarket} buyers)</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          data-testid={`delete-market-${market.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick('buyer_market', market.id, market.name)}
                          className="text-red-600 hover:bg-red-50"
                          disabled={buyersInMarket > 0}
                          title={buyersInMarket > 0 ? 'Cannot delete: market has buyers assigned' : 'Delete market'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`history-market-${market.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setHistoryDialog({ open: true, record: market })}
                          className="text-gray-400 hover:text-maroon-600 hover:bg-maroon-50"
                          title="View history"
                        >
                          <Clock className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <p className="text-xs text-maroon-500 text-center">
              Showing {filteredBuyerMarkets.length} of {buyerMarkets.length} markets
            </p>
          </CardContent>
        </Card>
        
        {/* Delete Dialog */}
        {renderDeleteDialog()}
        {renderExportDialog()}
        {renderHistoryDialog()}
      </div>
    );
  }

  // ==================== BUYERS VIEW ====================
  if (selectedCategory === 'buyers') {
    const openAddBuyerDialog = () => {
      setEditingBuyer(null);
      setBuyerForm({ name: '', market_id: '' });
      setBuyerDialogOpen(true);
    };

    const openEditBuyerDialog = (buyer) => {
      setEditingBuyer(buyer);
      setBuyerForm({
        name: buyer.name,
        market_id: buyer.market_id || ''
      });
      setBuyerDialogOpen(true);
    };

    return (
      <div className="space-y-4" data-testid="buyers-master-page">
        {renderHeader()}
        
        <Card className={`border-l-4 ${currentConfig.borderColor}`}>
          <CardContent className="p-4 lg:p-3 space-y-4">
            {/* Search */}
            {renderSearchBar()}
            
            {/* Add new buyer button */}
            <Button
              data-testid="add-buyer-button"
              onClick={openAddBuyerDialog}
              className={currentConfig.buttonColor}
              disabled={buyerMarkets.length === 0}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Buyer
            </Button>
            
            {buyerMarkets.length === 0 && (
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                Please add buyer markets first before creating buyers.
              </p>
            )}
            
            {/* List */}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {filteredBuyers.length === 0 ? (
                <p className="text-sm text-maroon-600 text-center py-8">
                  {searchQuery ? 'No buyers match your search' : 'No buyers added yet'}
                </p>
              ) : (
                filteredBuyers.map((buyer) => (
                  <div
                    key={buyer.id}
                    className="flex items-center justify-between p-3 border border-maroon-200 rounded-lg hover:bg-maroon-50"
                  >
                    <div>
                      <span className="font-medium text-maroon-950">{buyer.name}</span>
                      {buyer.market_name && (
                        <span className="text-xs text-gray-500 ml-2 flex items-center gap-1 inline-flex">
                          <Store className="h-3 w-3" />
                          {buyer.market_name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        data-testid={`edit-buyer-${buyer.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditBuyerDialog(buyer)}
                        className="text-maroon-600 hover:bg-maroon-50"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`delete-buyer-${buyer.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick('buyer', buyer.id, buyer.name)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`history-buyer-${buyer.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryDialog({ open: true, record: buyer })}
                        className="text-gray-400 hover:text-maroon-600 hover:bg-maroon-50"
                        title="View history"
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <p className="text-xs text-maroon-500 text-center">
              Showing {filteredBuyers.length} of {buyers.length} buyers
            </p>
          </CardContent>
        </Card>
        
        {/* Buyer Dialog */}
        <Dialog open={buyerDialogOpen} onOpenChange={setBuyerDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-emerald-600" />
                {editingBuyer ? 'Edit Buyer' : 'Add New Buyer'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="buyer-name">Buyer Name *</Label>
                <Input
                  id="buyer-name"
                  data-testid="buyer-name-input"
                  value={buyerForm.name}
                  onChange={(e) => setBuyerForm({ ...buyerForm, name: e.target.value })}
                  placeholder="Enter buyer name"
                />
              </div>
              <div>
                <Label htmlFor="buyer-market">Market *</Label>
                <MasterDataAutocomplete
                  value={buyerForm.market_id}
                  onValueChange={(value) => setBuyerForm({ ...buyerForm, market_id: value })}
                  options={buyerMarkets.map((market) => ({ value: market.id, label: market.name }))}
                  placeholder="Select a market"
                  searchPlaceholder="Search markets..."
                  emptyMessage="No matching market in Master Data"
                  testId="buyer-market-select"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBuyerDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveBuyer} className="bg-emerald-600 hover:bg-emerald-700">
                {editingBuyer ? 'Update' : 'Add'} Buyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Dialog */}
        {renderDeleteDialog()}
        {renderExportDialog()}
        {renderHistoryDialog()}
      </div>
    );
  }

  // ==================== SUPPLIERS VIEW ====================
  if (selectedCategory === 'suppliers') {
    return (
      <div className="space-y-4" data-testid="suppliers-master-page">
        {renderHeader()}
        
        <Card className={`border-l-4 ${currentConfig.borderColor}`}>
          <CardContent className="p-4 lg:p-3 space-y-4">
            {/* Search */}
            {renderSearchBar()}
            
            {/* Add button */}
            <Button
              data-testid="add-supplier-button"
              onClick={openAddSupplierDialog}
              className={`w-full ${currentConfig.buttonColor}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add New Supplier
            </Button>
            
            {/* List */}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {filteredSuppliers.length === 0 ? (
                <p className="text-sm text-maroon-600 text-center py-8">
                  {searchQuery ? 'No suppliers match your search' : 'No suppliers added yet'}
                </p>
              ) : (
                filteredSuppliers.map((supplier) => (
                  <div
                    key={supplier.id}
                    className="flex items-start justify-between p-3 border border-maroon-200 rounded-lg hover:bg-maroon-50"
                  >
                    <div className="flex-1">
                      <span className="font-medium text-maroon-950">{supplier.name}</span>
                      {(supplier.city || supplier.market) && (
                        <div className="flex items-center gap-2 mt-1 text-sm text-maroon-600">
                          <MapPin className="h-3 w-3" />
                          <span>{[supplier.city, supplier.market].filter(Boolean).join(' - ')}</span>
                        </div>
                      )}
                      {supplier.address && (
                        <p className="text-xs text-maroon-500 mt-1">{supplier.address}</p>
                      )}
                      {supplier.default_purchase_cd_percent > 0 && (
                        <span className="inline-block mt-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                          Default CD {supplier.default_purchase_cd_percent}%
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        data-testid={`edit-supplier-${supplier.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditSupplierDialog(supplier)}
                        className="text-maroon-600 hover:bg-maroon-100"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`delete-supplier-${supplier.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick('supplier', supplier.id, supplier.name)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`history-supplier-${supplier.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryDialog({ open: true, record: supplier })}
                        className="text-gray-400 hover:text-maroon-600 hover:bg-maroon-50"
                        title="View history"
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <p className="text-xs text-maroon-500 text-center">
              Showing {filteredSuppliers.length} of {suppliers.length} suppliers
            </p>
          </CardContent>
        </Card>
        
        {/* Supplier Dialog */}
        {renderSupplierDialog()}
        {renderDeleteDialog()}
        {renderExportDialog()}
        {renderHistoryDialog()}
      </div>
    );
  }

  // ==================== TRADE NAMES VIEW ====================
  if (selectedCategory === 'tradeNames') {
    return (
      <div className="space-y-4" data-testid="tradenames-master-page">
        {renderHeader()}
        
        <Card className={`border-l-4 ${currentConfig.borderColor}`}>
          <CardContent className="p-4 lg:p-3 space-y-4">
            {/* Search */}
            {renderSearchBar()}
            
            {/* Add button */}
            <Button
              data-testid="add-tradename-button"
              onClick={openAddTradeNameDialog}
              className={`w-full ${currentConfig.buttonColor}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add New Trade Name
            </Button>
            
            {/* List */}
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {filteredTradeNames.length === 0 ? (
                <p className="text-sm text-maroon-600 text-center py-8">
                  {searchQuery ? 'No trade names match your search' : 'No trade names added yet'}
                </p>
              ) : (
                filteredTradeNames.map((tradeName) => (
                  <div
                    key={tradeName.id}
                    data-testid={`tradename-item-${tradeName.id}`}
                    className={`flex items-start justify-between p-3 border rounded-lg hover:bg-maroon-50 ${
                      tradeName.is_active !== false ? 'border-maroon-200' : 'border-gray-300 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-maroon-950">{tradeName.name}</p>
                        {tradeName.supplier_name && (
                          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                            {tradeName.supplier_name}
                          </span>
                        )}
                        {tradeName.is_active === false && (
                          <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Inactive</span>
                        )}
                        {/* Show Dyed ordering mode badge for Dyed trade names */}
                        {tradeName.fabric_type === 'Dyed' && (
                          tradeName.dyed_ordering_mode === 'fixed_chart' ? (
                            <span className="px-2 py-0.5 text-xs bg-rose-100 text-rose-700 rounded">
                              📊 Fixed Chart ({tradeName.default_matching || '?'})
                            </span>
                          ) : tradeName.dyed_ordering_mode === 'pick_choose' ? (
                            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                              🎨 Pick & Choose
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded font-semibold">
                              ⚠️ Unassigned Mode
                            </span>
                          )
                        )}
                      </div>
                      <p className="text-xs text-maroon-600 mt-1">
                        {tradeName.fabric_type || 'N/A'} • {tradeName.si_unit || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        data-testid={`edit-tradename-${tradeName.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditTradeNameDialog(tradeName)}
                        className="text-maroon-600 hover:bg-maroon-100"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`delete-tradename-${tradeName.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick('trade_name', tradeName.id, tradeName.name)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`history-tradename-${tradeName.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryDialog({ open: true, record: tradeName })}
                        className="text-gray-400 hover:text-maroon-600 hover:bg-maroon-50"
                        title="View history"
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <p className="text-xs text-maroon-500 text-center">
              Showing {filteredTradeNames.length} of {tradeNames.length} trade names
            </p>
          </CardContent>
        </Card>
        
        {/* Trade Name Dialog */}
        {renderTradeNameDialog()}
        {renderDeleteDialog()}
        {renderExportDialog()}
        {renderHistoryDialog()}
      </div>
    );
  }

  // ==================== TRANSPORTERS VIEW ====================
  if (selectedCategory === 'transporters') {
    return (
      <div className="space-y-4" data-testid="transporters-master-page">
        {renderHeader()}
        
        <Card className={`border-l-4 ${currentConfig.borderColor}`}>
          <CardContent className="p-4 lg:p-3 space-y-4">
            {/* Search */}
            {renderSearchBar()}
            
            {/* Add button */}
            <Button
              data-testid="add-transporter-button"
              onClick={openAddTransporterDialog}
              className={`w-full ${currentConfig.buttonColor}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add New Transporter
            </Button>
            
            {/* List */}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {filteredTransporters.length === 0 ? (
                <p className="text-sm text-maroon-600 text-center py-8">
                  {searchQuery ? 'No transporters match your search' : 'No transporters added yet'}
                </p>
              ) : (
                filteredTransporters.map((transporter) => (
                  <div
                    key={transporter.id}
                    className={`flex items-center justify-between p-3 border rounded-lg hover:bg-maroon-50 ${
                      transporter.is_active ? 'border-maroon-200' : 'border-gray-300 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-maroon-950">{transporter.name}</p>
                        {!transporter.is_active && (
                          <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Inactive</span>
                        )}
                      </div>
                      <p className="text-xs text-maroon-600 mt-1">
                        {transporter.source} → {transporter.final_destination}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditTransporterDialog(transporter)}
                        className="text-maroon-600 hover:bg-maroon-50"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`delete-transporter-${transporter.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick('transporter', transporter.id, transporter.name)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`history-transporter-${transporter.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryDialog({ open: true, record: transporter })}
                        className="text-gray-400 hover:text-maroon-600 hover:bg-maroon-50"
                        title="View history"
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <p className="text-xs text-maroon-500 text-center">
              Showing {filteredTransporters.length} of {transporters.length} transporters
            </p>
          </CardContent>
        </Card>
        
        {/* Transporter Dialog */}
        {renderTransporterDialog()}
        {renderDeleteDialog()}
        {renderExportDialog()}
        {renderHistoryDialog()}
      </div>
    );
  }

  // ==================== DIALOGS ====================
  function renderTradeNameDialog() {
    return (
      <Dialog open={tradeNameDialogOpen} onOpenChange={setTradeNameDialogOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTradeName ? 'Edit Trade Name' : 'Add New Trade Name'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="tradename-name">Trade Name *</Label>
              <Input
                id="tradename-name"
                data-testid="tradename-name-input"
                value={tradeNameForm.name}
                onChange={(e) => setTradeNameForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter trade name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="tradename-supplier">Supplier</Label>
              <MasterDataAutocomplete
                value={tradeNameForm.supplier_id}
                onValueChange={(value) => setTradeNameForm(prev => ({ ...prev, supplier_id: value }))}
                options={[
                  { value: '__unassigned__', label: 'Unassigned', pinned: true },
                  ...suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name })),
                ]}
                placeholder="Select supplier (optional)..."
                searchPlaceholder="Search suppliers..."
                emptyMessage="No matching supplier in Master Data"
                className="mt-1"
                testId="tradename-supplier-select"
              />
              <p className="text-xs text-maroon-500 mt-1">Same trade name can exist for different suppliers</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tradename-fabrictype">Fabric Type *</Label>
                <Select
                  value={tradeNameForm.fabric_type}
                  onValueChange={(value) => setTradeNameForm(prev => ({ ...prev, fabric_type: value }))}
                >
                  <SelectTrigger data-testid="tradename-fabrictype-select" className="mt-1">
                    <SelectValue placeholder="Select fabric type..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    {fabricTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tradename-unit">Unit *</Label>
                <Select
                  value={tradeNameForm.si_unit}
                  onValueChange={(value) => setTradeNameForm(prev => ({ ...prev, si_unit: value }))}
                >
                  <SelectTrigger data-testid="tradename-unit-select" className="mt-1">
                    <SelectValue placeholder="Select unit..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Metre">Metre</SelectItem>
                    <SelectItem value="Kg">Kg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Dyed Ordering Mode - Only shown when Fabric Type is Dyed */}
            {tradeNameForm.fabric_type === 'Dyed' && (
              <div className="border border-rose-200 rounded-lg p-3 bg-rose-50/50">
                <p className="text-xs font-semibold text-maroon-700 mb-2">Dyed Ordering Settings</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dyed-ordering-mode">Ordering Mode *</Label>
                    <Select
                      value={tradeNameForm.dyed_ordering_mode}
                      onValueChange={(value) => setTradeNameForm(prev => ({ 
                        ...prev, 
                        dyed_ordering_mode: value,
                        // Reset default matching when switching to pick_choose
                        default_matching: value === 'pick_choose' ? '' : prev.default_matching
                      }))}
                    >
                      <SelectTrigger data-testid="dyed-ordering-mode-select" className="mt-1">
                        <SelectValue placeholder="Select mode..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="fixed_chart">📊 Fixed Chart</SelectItem>
                        <SelectItem value="pick_choose">🎨 Pick & Choose</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-maroon-500 mt-1">
                      {tradeNameForm.dyed_ordering_mode === 'fixed_chart' 
                        ? 'Single line: Qty × Matching = Total'
                        : tradeNameForm.dyed_ordering_mode === 'pick_choose'
                        ? 'Manual color-wise row entry'
                        : 'Select how this trade name is ordered'}
                    </p>
                  </div>
                  {tradeNameForm.dyed_ordering_mode === 'fixed_chart' && (
                    <div>
                      <Label htmlFor="default-matching">Default Matching *</Label>
                      <Input
                        id="default-matching"
                        data-testid="default-matching-input"
                        type="number"
                        min="1"
                        value={tradeNameForm.default_matching}
                        onChange={(e) => setTradeNameForm(prev => ({ ...prev, default_matching: e.target.value }))}
                        placeholder="e.g., 6"
                        className="mt-1"
                      />
                      <p className="text-xs text-maroon-500 mt-1">Pre-filled in Order Entry</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Add New Fabric Type */}
            <div className="border-t pt-3">
              <p className="text-xs text-maroon-600 mb-2">Don&apos;t see your fabric type? Add a new one:</p>
              <div className="flex gap-2">
                <Input
                  data-testid="new-fabric-type-input"
                  placeholder="New fabric type..."
                  value={newFabricTypeInput}
                  onChange={(e) => setNewFabricTypeInput(e.target.value)}
                  className="flex-1 h-8 text-sm"
                />
                <Button
                  data-testid="add-fabric-type-button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddFabricType}
                  className="h-8"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <Label htmlFor="tradename-active">Active Status</Label>
              <Switch
                id="tradename-active"
                data-testid="tradename-active-switch"
                checked={tradeNameForm.is_active}
                onCheckedChange={(checked) => setTradeNameForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setTradeNameDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTradeName} className="bg-rose-600 hover:bg-rose-700">
              {editingTradeName ? 'Update Trade Name' : 'Add Trade Name'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function renderSupplierDialog() {
    return (
      <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="supplier-name">Supplier Name *</Label>
              <Input
                id="supplier-name"
                data-testid="supplier-name-input"
                value={supplierForm.name}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter supplier name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="supplier-address">Address</Label>
              <Input
                id="supplier-address"
                data-testid="supplier-address-input"
                value={supplierForm.address}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Enter full address"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="supplier-city">City</Label>
                <Select
                  value={supplierForm.city}
                  onValueChange={(value) => setSupplierForm(prev => ({ ...prev, city: value }))}
                >
                  <SelectTrigger data-testid="supplier-city-select" className="mt-1">
                    <SelectValue placeholder="Select city..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    {INDIAN_CITIES.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="supplier-market">Market</Label>
                <Select
                  value={supplierForm.market}
                  onValueChange={(value) => setSupplierForm(prev => ({ ...prev, market: value }))}
                >
                  <SelectTrigger data-testid="supplier-market-select" className="mt-1">
                    <SelectValue placeholder="Select market..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    {TEXTILE_MARKETS.map((market) => (
                      <SelectItem key={market} value={market}>{market}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="supplier-default-cd">Default Purchase CD%</Label>
              <Input
                id="supplier-default-cd"
                data-testid="supplier-default-cd-input"
                type="text"
                inputMode="decimal"
                value={supplierForm.default_purchase_cd_percent}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, default_purchase_cd_percent: e.target.value }))}
                placeholder="e.g. 2.5"
                className="mt-1"
              />
              <p className="text-xs text-maroon-500 mt-1">Auto-fills Purchase CD% on new orders for this supplier's trade names. Still editable per order.</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSupplierDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSupplier} className="bg-amber-600 hover:bg-amber-700">
              {editingSupplier ? 'Update Supplier' : 'Add Supplier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function renderTransporterDialog() {
    return (
      <Dialog open={transporterDialogOpen} onOpenChange={setTransporterDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{editingTransporter ? 'Edit Transporter' : 'Add New Transporter'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="transporter-name">Transporter Name *</Label>
              <Input
                id="transporter-name"
                value={transporterForm.name}
                onChange={(e) => setTransporterForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter transporter name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="source">Source *</Label>
              <Input
                id="source"
                value={transporterForm.source}
                onChange={(e) => setTransporterForm(prev => ({ ...prev, source: e.target.value }))}
                placeholder="Enter source location"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="destination">Final Destination *</Label>
              <Input
                id="destination"
                value={transporterForm.final_destination}
                onChange={(e) => setTransporterForm(prev => ({ ...prev, final_destination: e.target.value }))}
                placeholder="Enter final destination"
                className="mt-1"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is-active">Active Status</Label>
              <Switch
                id="is-active"
                checked={transporterForm.is_active}
                onCheckedChange={(checked) => setTransporterForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
            <Button onClick={handleSaveTransporter} className="w-full bg-cyan-600 hover:bg-cyan-700">
              {editingTransporter ? 'Update Transporter' : 'Add Transporter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  function renderDeleteDialog() {
    return (
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, category: '', itemId: '', itemName: '' })}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteDialog.itemName}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  function renderExportDialog() {
    const exportData = getExportData('current');
    const allData = getExportData('all');
    return (
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleExport}
        title={`Export ${exportData?.name || 'Data'}`}
        hasFilters={!!searchQuery}
        filterDescription={searchQuery ? `Search: ${searchQuery}` : ''}
        totalRecords={allData?.all?.length || 0}
        filteredRecords={exportData?.filtered?.length || 0}
      />
    );
  }

  function renderHistoryDialog() {
    return (
      <RecordHistoryDialog
        open={historyDialog.open}
        onOpenChange={(open) => !open && setHistoryDialog({ open: false, record: null })}
        record={historyDialog.record}
        title="Record History"
      />
    );
  }

  return null;
}
